import { white } from '@kmns-internal/furikake/tokens'
import AddIcon from '@mui/icons-material/Add'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import date from 'src/exShared/util/date'
import { semanticColors } from 'src/features/theme/semanticColors'
import { useQueryMasters } from './api/useQueryMasters'
import { MasterAddModal } from './components/MasterAddModal/MasterAddModal'
import { MasterRowActionMenu } from './components/MasterRowActionMenu/MasterRowActionMenu'
import { MASTER_LIMIT } from './constants'

const Masters: FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const { data, isLoading, isError } = useQueryMasters()
  const history = useHistory()
  const location = useLocation()

  const keywordFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('keyword') ?? ''
  }, [location.search])

  const [inputValue, setInputValue] = useState(keywordFromUrl)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setInputValue(keywordFromUrl)
  }, [keywordFromUrl])

  const masters = useMemo(() => data?.masters ?? [], [data?.masters])
  const existingNames = useMemo(
    () => (data?.masters ?? []).map(m => m.name),
    [data?.masters],
  )
  const isMasterLimitReached = masters.length >= MASTER_LIMIT

  const filteredMasters = useMemo(() => {
    const trimmed = keywordFromUrl.trim()
    if (!trimmed) return masters
    return masters.filter(m =>
      m.name.toLowerCase().includes(trimmed.toLowerCase()),
    )
  }, [masters, keywordFromUrl])

  const applyFilter = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      setInputValue(trimmed)
      const params = new URLSearchParams(location.search)
      if (trimmed) {
        params.set('keyword', trimmed)
      } else {
        params.delete('keyword')
      }
      const newSearch = params.toString()
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      })
    },
    [history, location.pathname, location.search],
  )

  const isFilterActive = keywordFromUrl.trim().length > 0
  const hasNoFilterResults = isFilterActive && filteredMasters.length === 0

  return (
    <Stack sx={{ height: 'calc(100vh - var(--banner-height, 0px))' }}>
      <Box
        sx={{
          height: 72,
          width: '100%',
          alignItems: 'center',
          boxSizing: 'border-box',
          display: 'block',
          flexDirection: 'row',
        }}
      >
        <Header>
          <HeaderTitle title="製品マスタ" />
        </Header>
      </Box>

      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2, pl: 1.5, pr: 3, gap: 2 }}
      >
        <TextField
          size="small"
          placeholder="マスタ名で検索"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              applyFilter(inputValue)
            }
          }}
          onBlur={() => applyFilter(inputValue)}
          sx={{ width: 360, '& .MuiOutlinedInput-input': { px: 1.5 } }}
          data-testid="master-search-input"
          inputProps={{ 'aria-label': 'マスタ名で検索' }}
          InputProps={{
            endAdornment: <SearchOutlinedIcon color="action" />,
          }}
        />
        <Tooltip
          title={
            isMasterLimitReached
              ? `マスタは${MASTER_LIMIT}個まで作成できます`
              : ''
          }
          arrow
        >
          <span>
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddModalOpen(true)}
              disabled={isMasterLimitReached}
              data-testid="master-add-button"
            >
              マスタを追加
            </Button>
          </span>
        </Tooltip>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
            bgcolor: white,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
            bgcolor: white,
          }}
        >
          <ErrorMessage noMargin>エラーが発生しました</ErrorMessage>
        </Box>
      ) : (
        <TableContainer
          sx={{
            overflow: 'auto',
            position: 'relative',
            flexGrow: 1,
            bgcolor: white,
          }}
        >
          <Table sx={{ bgcolor: white, width: '100%' }} stickyHeader>
            <TableHead sx={{ backgroundColor: white }}>
              <TableRow>
                <TableCell
                  size="small"
                  sx={{
                    whiteSpace: 'normal',
                    backgroundColor: white,
                  }}
                >
                  マスタ名
                </TableCell>
                <TableCell
                  size="small"
                  sx={{
                    width: '18%',
                    backgroundColor: white,
                  }}
                >
                  作成日時
                </TableCell>
                <TableCell
                  size="small"
                  sx={{
                    width: '18%',
                    backgroundColor: white,
                  }}
                >
                  データ更新日時
                </TableCell>
                <TableCell
                  size="small"
                  sx={{
                    width: 88,
                    py: 1,
                    px: 3,
                    backgroundColor: white,
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMasters.map(master => (
                <TableRow
                  key={master.uuid}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: semanticColors.colorBgTextHover,
                    },
                  }}
                  onClick={() => history.push(`/masters/${master.uuid}`)}
                  data-testid={`master-row-${master.uuid}`}
                >
                  <TableCell
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography variant="body2">{master.name}</Typography>
                  </TableCell>
                  <TableCell size="small">
                    {date.formatYYYYMMDDHHMM(master.createdAt)}
                  </TableCell>
                  <TableCell size="small">
                    {master.dataUpdatedAt instanceof Date &&
                    master.dataUpdatedAt.getTime() > 0
                      ? date.formatYYYYMMDDHHMM(master.dataUpdatedAt)
                      : undefined}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 88,
                      py: 1,
                      px: 3,
                      cursor: 'default',
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <MasterRowActionMenu
                      masterUuid={master.uuid}
                      masterName={master.name}
                      existingNames={existingNames}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {hasNoFilterResults && (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      一致するマスタがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isFilterActive && masters.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      マスタがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <MasterAddModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        existingNames={existingNames}
      />
    </Stack>
  )
}

export default Masters
